import { TopalParty, TopalCreditor, TopalPayMethod } from './topal-xml-builder';
import { TopalValidationError } from './topal-validation-error';

export interface EmployeeInput {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ReimbursementInput {
  receiverIban: string;
  receiverStreet: string;
  receiverHouseNumber: string;
  receiverCity: string;
  receiverPostalCode: string;
  receiverCountry?: string | null;
}

export interface EmployeePartyData {
  party: TopalParty;
  creditor: TopalCreditor;
  payMethod: TopalPayMethod;
}

function truncate(value: string, maxLength: number): string {
  return value.substring(0, maxLength);
}

export function formatIban(iban: string): string {
  return iban.toUpperCase().replaceAll(' ', '');
}

function extractBankCode(formattedIban: string): string {
  if (formattedIban.startsWith('CH') || formattedIban.startsWith('LI')) {
    return formattedIban.substring(4, 9).replace(/^0+/, '') || '0';
  }
  return '';
}

function generateFreePartyNum(userId: string): string {
  const segment = userId.split('-')[0] || userId;
  return truncate(`EXP-${segment}`, 22);
}

export function mapEmployeeToTopalParty(
  user: EmployeeInput,
  reimbursement: ReimbursementInput,
): EmployeePartyData {
  const lastNamePart = user.lastName || '';
  const firstInitial = (user.firstName || '').substring(0, 1);
  const employeeLabel =
    lastNamePart || firstInitial
      ? `${lastNamePart}${firstInitial ? ` ${firstInitial}.` : ''}`.trim()
      : user.id;
  if (!reimbursement) {
    throw new TopalValidationError(
      `Keine Rückerstattungsdaten für Mitarbeiter ${employeeLabel}`,
    );
  }
  if (!reimbursement.receiverIban) {
    throw new TopalValidationError(`IBAN fehlt für Mitarbeiter ${employeeLabel}`);
  }
  if (!user.firstName || !user.lastName) {
    throw new TopalValidationError(`Name fehlt für Mitarbeiter ${employeeLabel}`);
  }

  const freePartyNum = generateFreePartyNum(user.id);
  const formattedIban = formatIban(reimbursement.receiverIban);
  const fullName = `${user.firstName} ${user.lastName}`;

  const party: TopalParty = {
    languageCode: 'DE',
    countryCode: reimbursement.receiverCountry
      ? String(reimbursement.receiverCountry).toUpperCase()
      : 'CH',
    shortName: truncate(fullName, 22),
    name: truncate(fullName, 60),
    zip: truncate(reimbursement.receiverPostalCode, 22),
    city: truncate(reimbursement.receiverCity, 22),
    telephone: '',
    telefax: '',
    address1: truncate(
      `${reimbursement.receiverStreet ?? ''} ${reimbursement.receiverHouseNumber ?? ''}`.trim(),
      36,
    ),
    address2: '',
    email: user.email,
    web: '',
    line1: '',
    line2: '',
    freePartyNum,
  };

  const creditor: TopalCreditor = {
    freePartyNum,
    payTermCode: '30T',
    payMethodName: 'IBAN-CHF',
  };

  const payMethod: TopalPayMethod = {
    freePartyNum,
    personRole: 3,
    accountCode: '',
    payTypeFID: 9,
    name: 'IBAN-CHF',
    accountNum: formattedIban,
    bankCode: extractBankCode(formattedIban),
  };

  return { party, creditor, payMethod };
}
